<template>
  <div v-show="isVisible" class="popup-overlay" :class="{ active: isVisible }">
    <div
      class="popup-content relative"
      :class="size"
      :style="maxWidth ? { maxWidth: maxWidth + 'px' } : {}"
    >
      <button @click="closePopup" class="popup-close-btn">
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M18 6L6 18M6 6L18 18"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>
      <div class="popup-body relative" ref="popupBody">
        <!-- Slot for dynamic content -->
        <slot></slot>
      </div>
      <!-- <div class="popup-footer">
        <button @click="closePopup" class="popup-close-footer-btn">
          Close
        </button>
      </div> -->
    </div>
  </div>
</template>

<script>
export default {
  props: {
    title: String,
    isVisible: Boolean,
    size: String,
    maxWidth: Number,
  },
  watch: {
    /**
     * Watches for changes in isVisible prop to manage HTML freeze class
     * @param {Boolean} newVal - New visibility state
     * @param {Boolean} oldVal - Previous visibility state
     */
    isVisible(newVal, oldVal) {
      if (newVal) {
        this.addFreezeClass();
        // Ensure popup body scrolls to top when becoming visible
        this.$nextTick(() => {
          this.scrollPopupBodyToTop();
        });
      } else {
        this.removeFreezeClassIfNoActivePopups();
      }
    },
  },
  methods: {
    /**
     * Closes the popup by emitting a close event
     */
    closePopup() {
      this.$emit("close");
    },
    /**
     * Adds freeze class to HTML tag to prevent body scrolling
     */
    addFreezeClass() {
      document.documentElement.classList.add('freeze');
    },
    /**
     * Removes freeze class from HTML tag only if no other active popups exist
     */
    removeFreezeClassIfNoActivePopups() {
      // Count active popup overlays
      const activePopups = document.querySelectorAll('.popup-overlay.active');
      if (activePopups.length <= 1) {
        document.documentElement.classList.remove('freeze');
      }
    },
    /**
     * Scrolls the popup body container to the top
     */
    scrollPopupBodyToTop() {
      const el = this.$refs.popupBody;
      if (el) {
        el.scrollTop = 0;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~/scss/include/_commonInclude.scss";

.content .app {
  .popup-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.6);
    backdrop-filter: blur(2px);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 9999;
    animation: fadeIn 0.3s ease-out;
  }

  // .popup-content__inner {
  //   border: 1px solid #d2b49d;
  //   padding: rem(16);
  //   border-radius: rem(20);
  // }

  .popup-content {
    background-color: color(white);
    background-image: url("https://s3.ap-southeast-1.amazonaws.com/nespresso.web/2025double11/assets/images/popup-background.png");
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    padding: rem(24);
    border-radius: rem(12);
    box-shadow: 0 rem(8) rem(32) rgba(0, 0, 0, 0.15);
    width: 90%;
    max-width: rem(600);
    max-height: 85vh;
    display: flex;
    flex-direction: column;
    position: relative;
    animation: slideUp 0.3s ease-out;

    padding-top: 48px;

    &.large {
      max-width: rem(1313);
    }

    &::before {
      content: "";
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: calc(100% - 32px);
      height: calc(100% - 32px);
      border-radius: rem(20);
      border: 1px solid #d2b49d;
    }

    @include media-breakpoint-down(md) {
      width: 95%;
      padding: rem(20);
      border-radius: rem(8);
    }
  }

  .popup-header {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    margin-bottom: rem(10);
    // padding-bottom: rem(16);
    // border-bottom: 1px solid color(lightBorderGray);

    h3 {
      margin: 0;
      color: color(darkText);
      font-size: rem(20);
      font-weight: 600;
      line-height: 1.3;

      @include media-breakpoint-down(md) {
        font-size: rem(18);
      }
    }
  }

  .popup-close-btn {
    background: none;
    border: none;
    padding: rem(8);
    cursor: pointer;
    color: color(mediumGray);
    border-radius: rem(6);
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    width: rem(40);
    height: rem(40);
    position: absolute;
    top: rem(16);
    right: rem(16);
    z-index: 100;

    &:hover {
      //   background-color: color(lightGray);
      color: color(darkText);
      transform: scale(1.05);
    }

    &:active {
      transform: scale(0.95);
    }

    svg {
      width: rem(20);
      height: rem(20);
    }
  }

  .popup-body {
    flex-grow: 1;
    overflow-y: auto;
    padding-right: rem(8);
    margin-right: rem(-8);

    &::-webkit-scrollbar {
      width: rem(6);
    }

    &::-webkit-scrollbar-track {
      background: color(lightGray);
      border-radius: rem(3);
    }

    &::-webkit-scrollbar-thumb {
      background: color(mediumGray);
      border-radius: rem(3);

      &:hover {
        background: color(darkGray);
      }
    }
  }

  .popup-footer {
    margin-top: rem(20);
    padding-top: rem(16);
    border-top: 1px solid color(lightBorderGray);
    text-align: right;
  }

  .popup-close-footer-btn {
    background-color: color(nespressoBrown);
    color: color(white);
    border: none;
    padding: rem(12) rem(24);
    border-radius: rem(6);
    cursor: pointer;
    font-size: rem(14);
    font-weight: 500;
    transition: all 0.2s ease;

    &:hover {
      background-color: color(nespressoLightBrown);
      transform: translateY(rem(-1));
      box-shadow: 0 rem(4) rem(12) rgba(0, 0, 0, 0.15);
    }

    &:active {
      transform: translateY(0);
    }
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(rem(20)) scale(0.95);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
}
</style>
